import { useEffect, useState } from 'react';

interface Options {
  /** Contêiner rolável da lista: a interseção é medida em relação a ele, não à janela. */
  root: Element | null;
  /** Só observa enquanto carregar mais fizer sentido (há próxima página e nada em andamento/falho). */
  enabled: boolean;
  onReach: () => void;
  /**
   * Muda a cada página acrescentada à lista. Recria o observador mesmo que `enabled` não tenha
   * chegado a mudar de valor entre duas renderizações (respostas rápidas fazem o React Query
   * juntar "buscando" e "carregou" numa renderização só).
   */
  loadedPages: number;
}

/**
 * Chama `onReach` quando o sentinela (devolvido como ref de callback) chega perto do fim da área
 * rolável. Sem `IntersectionObserver` (ambiente antigo) não faz nada: o botão "Carregar mais"
 * continua cobrindo esse caso.
 *
 * O observador é recriado a cada página carregada (e quando `enabled` volta a ser verdadeiro). É
 * de propósito: o navegador só avisa quando a interseção MUDA, então, se a página recém-carregada
 * ainda não preencher a área visível, só um observador novo (que recebe o estado atual ao começar
 * a observar) pede a página seguinte. Enquanto `enabled` é falso (erro, busca em andamento) nada
 * é observado, e por isso uma falha não vira laço de requisições.
 */
export function useLoadMoreSentinel({
  root,
  enabled,
  onReach,
  loadedPages,
}: Options): (element: Element | null) => void {
  const [sentinel, setSentinel] = useState<Element | null>(null);

  useEffect(() => {
    if (!enabled || !root || !sentinel || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onReach();
      },
      // 200px de antecedência: a próxima página chega antes de a pessoa encostar no fim.
      { root, rootMargin: '0px 0px 200px 0px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [enabled, root, sentinel, onReach, loadedPages]);

  return setSentinel;
}
